/* eslint-disable @typescript-eslint/no-explicit-any */

import * as _ from "lodash";
import { JsonValue } from "type-fest";

import { memoize } from "@ctra/utils";
import { AIModelEventContext, Enterprise as App, EventStatus, UserPreferencesEntity } from "@ctra/api";
import { Enterprise as Content, i18nTranslate, IntlMessageFormat } from "@ctra/i18n";

import { EventBase } from "./Base";
import { ImpactTrackedEventPage } from "../event-page";
import { CustomEventCta, CustomEventExpandIcon, CustomEventView } from "@events";

const {
  kpi: { displayName }
} = Content;

interface TranslationObject {
  key: string;
  values?: Record<string, any>;
  params?: Record<string, any>;
  substitutions?: Record<string, any>;
  type?: string;
}

/**
 * Check if the object is a translation object
 * @param obj
 * @returns {obj is TranslationObject}
 */
const isTranslationObject = (obj: unknown): obj is TranslationObject => {
  return _.isObject(obj) && "key" in obj;
};

/**
 * Translation resolver class to resolve translation objects
 */
class TranslationResolver {
  private unitSystem: string;

  constructor(unitSystem: string) {
    this.unitSystem = unitSystem;
  }

  /**
   * Recursively resolve a translation object, going depth-first.
   * @param {TranslationObject} translationObj
   * @returns {string}
   */
  resolveTranslation(translationObj: TranslationObject): string {
    const substitutions: Record<string, string> = {};

    for (const [key, value] of Object.entries(translationObj.substitutions || {})) {
      if (typeof value === "object" && value !== null) {
        substitutions[key] = value[this.unitSystem.toLowerCase()];
      } else {
        substitutions[key] = value;
      }
    }

    return this.resolveNode(translationObj, substitutions);
  }

  /**
   * Function to format the key using params
   * @param {string} key
   * @param {Record<string, any>} params
   * @param {Record<string, string>} substitutions
   * @returns {string}
   */
  formatKey(key: string, params: Record<string, any>, substitutions: Record<string, string>): string {
    if (params && Object.keys(params).length > 0) {
      key = this.formatValues(key, substitutions, params);
    }
    return key;
  }

  /**
   * Resolves a single node in the JSON translation object.
   * @param {TranslationObject} node
   * @param {Record<string, string>} substitutions
   * @returns {string}
   */
  resolveNode(node: TranslationObject, substitutions: Record<string, string>): string {
    const nodeType = node.type || "asset";
    const key = node.key;
    const values = node.values || {};
    const params = node.params || {};

    if (nodeType === "asset") {
      const formattedKey = this.formatKey(key, params, substitutions);
      const apiResponse = i18nTranslate(formattedKey);

      return this.formatValues(apiResponse, substitutions, values);
    } else if (nodeType === "substitution") {
      return substitutions[key] || key;
    } else if (nodeType === "dynamic") {
      return this.formatValues(key, substitutions, values);
    } else {
      return key;
    }
  }

  /**
   * Format the ICU string with the given values.
   * @param {string} key
   * @param {Record<string, string>} substitutions
   * @param {Record<string, any>} values
   * @returns {string}
   */
  formatValues(key: string, substitutions: Record<string, string>, values: Record<string, any>): string {
    const overrideValues: Record<string, string> = {};

    for (const [valueKey, valueNode] of Object.entries(values)) {
      overrideValues[valueKey] = this.resolveNode(valueNode, substitutions);
    }

    const formatter = new IntlMessageFormat(key);
    const result = formatter.format(overrideValues);

    return _.isString(result) ? result : _.join(result, "\r\n");
  }
}

/**
 * AI model event (for chart annotations)
 */
class AIModelEvent extends EventBase<AIModelEventContext> {
  /**
   * Get the metadata for the event
   */
  @memoize
  getContext(): {
    status: EventStatus;
    translations: JsonValue;
    version: number;
  } {
    const {
      context: { impactTrackingState, translations, version }
    } = this.entity;

    const v = !!version ? parseFloat(version) : 1;

    return {
      translations: v === 1 ? null : JSON.parse(translations),
      status: impactTrackingState as EventStatus,
      version: v
    };
  }

  @memoize
  getDescription(): string {
    const { version } = this.getContext();

    return version === 1 ? this.getV1Description() : this.getV2Description();
  }

  @memoize
  getV1Description(): string {
    const { description: originalDescription } = this.entity;
    const parts = _.trim(originalDescription).split(/\[|\]/);

    const metrics = _.mapValues(
      _.pick(_.keyBy(App.entities.getDataDictionary(App.store.getState()), "dataProperties.typeName"), parts),
      (v, k) => i18nTranslate(displayName(k))
    );

    return _.join(
      _.map(parts, (part) => metrics[part] || part),
      ""
    );
  }

  @memoize
  getV2Description(): string {
    let result;

    const { unitSystem } = _.defaultTo(App.entities.getUserPreferences(App.store.getState()), {
      unitSystem: "SI"
    }) as UserPreferencesEntity;

    const { translations } = this.getContext();
    const resolver = new TranslationResolver(_.toLower(unitSystem));
    const isTranslatable = isTranslationObject(translations);

    if (isTranslatable) {
      result = resolver.resolveTranslation(translations);
    } else if (_.isArray(translations)) {
      result = _.join(
        _.compact(
          _.map(translations, (t) => (isTranslationObject(t) ? resolver.resolveTranslation(t) : null))
        ),
        "\r\n"
      );
    } else {
      result = "[NOT_TRANSLATABLE]";
    }

    return result;
  }

  /**
   * Get the components for the events
   */
  @memoize
  getComponents(): ReturnType<typeof EventBase.prototype.getComponents> {
    return {
      Page: ImpactTrackedEventPage,
      ExpandIcon: CustomEventExpandIcon,
      View: CustomEventView,
      CTA: CustomEventCta
    };
  }
}

export { AIModelEvent };
